import { useMemo } from 'react'
import type { ReactNode } from 'react'
import { useParams } from 'react-router'
import { useCountdown } from '../lib/clock'
import { DraftProvider, useDraftContext } from '../state/DraftProvider'
import ClockDisplay from '../components/board/ClockDisplay'
import JoinQr from '../components/board/JoinQr'
import LobbyView from '../components/board/LobbyView'
import OnDeckStrip from '../components/board/OnDeckStrip'
import PickFlash from '../components/board/PickFlash'
import PickGrid from '../components/board/PickGrid'
import { usePickFlash } from '../components/board/usePickFlash'
import { pickVerdict } from '../components/board/verdict'
import type { DraftState, PickState, PlayerSummary } from '../types/protocol'


// The TV board. Design target is a 3840x2160 panel in landscape fullscreen,
// and the rule that makes that work is that *every* dimension on this page is
// viewport-relative — see the token block in styles/application.css. There is
// deliberately no resolution branching anywhere: a 1080p window renders the
// same layout at half the CSS px and the same physical size on the same
// diagonal.
//
// It is also strictly read-only. Every control surface lives in the owner and
// commissioner views; nobody walks up to the TV to click something.

export default function Board() {
  const { code = '' } = useParams()
  return (
    <DraftProvider code={code}>
      <BoardView />
    </DraftProvider>
  )
}

function BoardView() {
  const { draft, players, loading, error } = useDraftContext()

  // Picks carry only player_id; names/positions/colours all come from the
  // pool the provider already fetched. Same join DraftRoom does.
  const playersById = useMemo(
    () => new Map(players.map((player) => [player.id, player])),
    [players],
  )

  // The pool arrives sorted by effective rank and the provider never re-sorts it,
  // so a player's 1-based position in it is the slot the board expected them at.
  // Built once per pool rather than scanned per flash — the pool is ~3,000 rows.
  const consensusSlotById = useMemo(
    () => new Map(players.map((player, index) => [player.id, index + 1])),
    [players],
  )

  // null while loading, so opening a board mid-draft does not immediately
  // replay the last pick as if it had just happened. The hook also decides
  // whether this pick earned its drum roll — see usePickFlash.
  const flash = usePickFlash(draft?.picks)

  if (loading) return <BoardMessage>Loading…</BoardMessage>
  if (error) return <BoardMessage>{error}</BoardMessage>
  if (!draft) return <BoardMessage>No draft data</BoardMessage>

  return (
    <main className="flex h-screen flex-col overflow-hidden">
      <BoardHeader draft={draft} />
      {draft.status === 'setup' ? (
        <LobbyView draft={draft} />
      ) : (
        <PickGrid draft={draft} playersById={playersById} />
      )}
      {flash && (
        <FlashOverlay
          draft={draft}
          pick={flash.pick}
          preroll={flash.preroll}
          playersById={playersById}
          consensusSlotById={consensusSlotById}
        />
      )}
    </main>
  )
}

// Header owns ~10vh: the on-the-clock team, the clock itself, who is next,
// and a small persistent QR so latecomers can still join off the screen.
function BoardHeader({ draft }: { draft: DraftState }) {
  const deadlineMs = draft.deadline_at ? new Date(draft.deadline_at).getTime() : null
  const { remainingMs, expired } = useCountdown(deadlineMs)

  const currentPick = draft.picks.find((pick) => pick.overall === draft.current_pick_overall)
  const currentTeam = currentPick
    ? (draft.teams.find((team) => team.id === currentPick.team_id) ?? null)
    : null
  const complete = draft.status === 'complete'

  return (
    <header className="flex h-[10vh] flex-none items-center gap-[2vw] border-b-[0.2vh] border-line bg-surface-2 px-[1.5vw] shadow-raised">
      <div className="flex min-w-0 flex-1 flex-col gap-[0.3vh]">
        <p className="text-board-label font-mono font-semibold tracking-[0.3em] text-signal uppercase">
          {complete ? 'Final' : currentTeam ? 'On the clock' : draft.name}
        </p>
        <p className="text-board-title font-display truncate leading-tight font-bold text-ink-hi">
          {complete ? draft.name : (currentTeam?.name ?? draft.name)}
        </p>
        {currentPick && !complete && (
          <p className="text-board-sub leading-none font-semibold text-ink-lo tabular-nums">
            Round {currentPick.round} · Pick {currentPick.slot} · #{currentPick.overall}
          </p>
        )}
      </div>

      {complete ? (
        <p className="text-board-clock font-display text-signal leading-none font-bold">
          COMPLETE
        </p>
      ) : (
        <ClockDisplay
          status={draft.status}
          remainingMs={remainingMs}
          expired={expired}
          pausedRemainingMs={draft.paused_remaining_ms}
          deadlineAt={draft.deadline_at}
          pickSeconds={draft.pick_seconds}
        />
      )}

      <div className="flex min-w-0 flex-1 items-center justify-end gap-[1.5vw]">
        {!complete && <OnDeckStrip draft={draft} />}
        <div className="flex flex-none flex-col items-center gap-[0.4vh]">
          <JoinQr code={draft.join_code} sizeClass="w-[4.2vw] h-[4.2vw]" />
          <p className="text-board-label font-mono font-semibold tracking-[0.2em] text-signal tabular-nums">
            {draft.join_code}
          </p>
        </div>
      </div>
    </header>
  )
}

function FlashOverlay({
  draft,
  pick,
  preroll,
  playersById,
  consensusSlotById,
}: {
  draft: DraftState
  pick: PickState
  preroll: boolean
  playersById: Map<number, PlayerSummary>
  consensusSlotById: Map<number, number>
}) {
  return (
    <PickFlash
      key={pick.overall}
      overall={pick.overall}
      round={pick.round}
      slot={pick.slot}
      preroll={preroll}
      player={pick.player_id != null ? (playersById.get(pick.player_id) ?? null) : null}
      team={draft.teams.find((team) => team.id === pick.team_id) ?? null}
      verdict={pickVerdict(
        pick.overall,
        pick.player_id != null ? consensusSlotById.get(pick.player_id) : undefined,
        draft.teams.length,
      )}
    />
  )
}

function BoardMessage({ children }: { children: ReactNode }) {
  return (
    <main className="flex h-screen items-center justify-center overflow-hidden">
      <p className="text-board-hero font-display font-semibold text-ink-lo">{children}</p>
    </main>
  )
}
